import React from "react";
import { graphql } from "gatsby";
import Page from "~/models/page";
import Frame from "~/views/layout/frame";
import Container from "~/views/layout/container";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Header from "~/views/compositions/navigation/header";
import Breadcrumb from "~/views/compositions/navigation/breadcrumb";
import ShareButtons from "~/views/components/generic/share-buttons";
import RecommendedResources from "~/views/compositions/resource/recommended";
import NewsletterSubscription from "~/views/components/newsletter/subscription";
import useScrollLogs from "~/helpers/use-scroll-logs";
import Tagline from "~/views/components/generic/tagline";
import MarkdownRenderer from "~/views/components/content/markdown";

type Props = {
  data: any;
};

const ResourcePage = ({ data }: Props) => {
  useScrollLogs("content_view");

  const page = new Page(data.page as any);

  return (
    <Frame>
      <Header />
      <Tagline />
      <Breadcrumb breadcrumb={page.breadcrumb} className="xl:max-w-[1328px]" />

      <Container className="pb-8 lg:pt-6 lg:pb-16 grid grid-cols-12 grid-gap-x">
        <div className="col-start-1 col-end-13 lg:col-end-10">
          <h1 className="mb-4 text-4xl sm:text-7xl lg:text-9xl font-bold leading-[1.4]">
            {page.title}
          </h1>
          <ShareButtons page={page} />
        </div>
      </Container>

      <Container className="grid grid-cols-12 grid-gap-x">
        <div className="col-start-1 col-end-13 lg:col-start-2 lg:col-end-10 xl:col-end-9">
          <div className="mb-16 sm:mb-[128px] sm:px-6 lg:px-[37px]">
            <div className="relative">
              <img
                src={page.imageUrls?.thumbnail?.publicURL}
                alt={page.title}
                loading="eager"
                className="mb-12 sm:mb-16 rounded-2xl isolate bg-neutral-99"
              />
            </div>
          </div>

          <MarkdownRenderer
            id="content"
            content={page.content}
            references={page.references}
          />
        </div>

        <div className="hidden lg:block col-start-10 col-end-13 sticky top-[106px]">
          <RecommendedResources currentResource={page} className="mb-[120px]" />
        </div>
      </Container>

      <NewsletterSubscription className="mt-[160px]" type="dark" />
    </Frame>
  );
};

export default ResourcePage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const sd = structuredData(page);

  return <SEO page={page} sd={sd} />;
};

export const query = graphql`
  query ResourcePage($id: String) {
    page: pageMdx(filter: { frontmatter: { id: { eq: $id } } }) {
      ...PageFields
    }
  }
`;
